import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Mail, Clock, Copy, Check, Linkedin, Github } from 'lucide-react';
import { nikoInfo } from '../../data/portfolioData';
import { useCursorContext } from '../../hooks/useCursorContext';

const socialIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  LinkedIn: Linkedin,
  GitHub: Github,
  Email: Mail,
};

export function ContactInfo() {
  const { setCursorState } = useCursorContext();
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(nikoInfo.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  return (
    <div className="space-y-12">
      {/* Email */}
      <div>
        <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          E-Mail
        </h3>
        <div className="flex items-center gap-4">
          <a
            href={`mailto:${nikoInfo.email}`}
            className="text-xl text-white hover:text-emerald-500 transition-colors"
            onMouseEnter={() => setCursorState('hovering')}
            onMouseLeave={() => setCursorState('default')}
          >
            {nikoInfo.email}
          </a>
          <button
            onClick={handleCopyEmail}
            onMouseEnter={() => setCursorState('hovering')}
            onMouseLeave={() => setCursorState('default')}
            className="p-2 rounded-lg border border-white/10 hover:border-emerald-500/50 hover:bg-emerald-500/10 transition-colors"
            aria-label="E-Mail kopieren"
          >
            <AnimatePresence mode="wait">
              {copied ? (
                <motion.span
                  key="check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                >
                  <Check className="w-4 h-4 text-emerald-500" />
                </motion.span>
              ) : (
                <motion.span
                  key="copy"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                >
                  <Copy className="w-4 h-4 text-white/40" />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Location */}
      <div>
        <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Standort
        </h3>
        <p className="text-lg text-white/70">{nikoInfo.location}</p>
      </div>

      {/* Availability */}
      <div>
        <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Verfügbarkeit
        </h3>
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
          </span>
          <span className="text-lg text-emerald-500">Verfügbar für neue Projekte</span>
        </div>
      </div>

      {/* Social Links */}
      <div>
        <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-4">
          Social
        </h3>
        <div className="flex gap-4">
          {nikoInfo.socials.map((social) => {
            const Icon = socialIcons[social.name];
            return (
              <motion.a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={() => setCursorState('hovering')}
                onMouseLeave={() => setCursorState('default')}
                className="w-12 h-12 flex items-center justify-center border border-white/10 rounded-full hover:border-emerald-500/50 hover:bg-emerald-500/10 transition-colors"
                aria-label={social.name}
              >
                {Icon && <Icon className="w-5 h-5 text-white/60 hover:text-emerald-500" />}
              </motion.a>
            );
          })}
        </div>
      </div>

      {/* Additional info */}
      <div className="pt-8 border-t border-white/10">
        <p className="text-white/40 text-sm leading-relaxed">
          {nikoInfo.fullBio}
        </p>
      </div>
    </div>
  );
}
